import { useState, useEffect, useRef } from "react";
import styles from "./dropdownfilter.module.css";
import DropDownIcon from "../Icons/DropDownIcon";

const MultiStatusSelect = ({
	statusOptions,
	options, // Support both 'options' and 'statusOptions' prop names
	defaultSelected = [],
	labelText = "",
	placeholder = "Select Status",
	dropdownClass = "",
	dropdownToggleClass = "",
	onChange = () => {},
}) => {
	// Use options prop if provided, otherwise use statusOptions, otherwise default
	const allStatusOptions = options || statusOptions || [
		{ label: "Confirmed" },
		{ label: "Unconfirmed" },
		{ label: "Checked in" },
		{ label: "Completed" },
		{ label: "Cancelled" },
		{ label: "No Show" },
	];
	const [selectedOptions, setSelectedOptions] = useState(defaultSelected);
	const [isDropdownOpen, setDropdownOpen] = useState(false);
	const dropdownRef = useRef(null);
	const dropdownMenuRef = useRef(null);
	// Track if user has interacted to distinguish between initial "All" and explicit "None"
	const hasUserInteracted = useRef(false);

	const toggleDropdown = () => {
		setDropdownOpen((prev) => !prev);
	};

	// Sync selectedOptions with defaultSelected prop when it changes (only if actually different)
	useEffect(() => {
		// Only update if the arrays are actually different
		// Use a ref to get the current value without including it in dependencies
		setSelectedOptions(prev => {
			const currentSorted = [...prev].sort().join(',');
			const newSorted = [...(defaultSelected || [])].sort().join(',');
			if (currentSorted !== newSorted) {
				// Reset interaction flag when defaultSelected changes externally
				hasUserInteracted.current = false;
				return defaultSelected;
			}
			return prev;
		});
	}, [defaultSelected]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				setDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Display label: show selected items, or "Select" if user has interacted and nothing selected, or placeholder if initial state
	const displayLabel = selectedOptions.length > 0 
		? selectedOptions.join(", ") 
		: (hasUserInteracted.current ? "Select" : placeholder);

	// When "All" is selected (empty array initially), treat all options as checked for display
	// But if user has interacted and array is empty, it means "none selected"
	const isAllSelected = selectedOptions.length === 0 && !hasUserInteracted.current;

	useEffect(() => {
		if (isDropdownOpen && dropdownMenuRef.current) {
			dropdownMenuRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [isDropdownOpen]);

	const labelToClassName = (label) => {
		return label
			.toLowerCase()
			.replace(/\s+/g, "-")
			.replace(/[()]/g, "")
			.replace(/[^a-z\-]/g, ""); // allow dash and lowercase only
	};

	const handleCheckboxChange = (value) => {
		hasUserInteracted.current = true;
		let updated;
		
		// If all are selected (empty array means "All" initially), unchecking one means select all except that one
		if (isAllSelected) {
			// Get all options except the one being unchecked
			// Handle both {label: "text"} and {text: "text"} formats
			updated = allStatusOptions
				.map(opt => opt.label || opt.text)
				.filter(label => label !== value);
		} else if (selectedOptions.includes(value)) {
			// Unchecking: remove the item
			updated = selectedOptions.filter((item) => item !== value);
			// Allow empty array - this means "none selected" (no status filter)
		} else {
			// Checking: add the item
			updated = [...selectedOptions, value];
		}
		
		// Only update if the value actually changed
		const currentSorted = [...selectedOptions].sort().join(',');
		const newSorted = [...updated].sort().join(',');
		if (currentSorted !== newSorted) {
			setSelectedOptions(updated);
			onChange(updated);
		}
	};

	return (
		<div
			ref={dropdownRef}
			className={`${dropdownClass} commonDropdownNoImage`}
			tabIndex={0}>
			<div className={`dropdownToggle ${dropdownToggleClass} ${isDropdownOpen ? styles.focused : ''} ${selectedOptions.length === 0 ? 'placeholder' : ""}`}
				onMouseDown={(e) => e.preventDefault()}
				onClick={toggleDropdown}>
				{labelText} {displayLabel}
				<div className={`${styles.arrow} ${isDropdownOpen ? styles.up : ''}`}>
					<DropDownIcon/>
				</div>
			</div>
			{isDropdownOpen && (
				<ul className="dropdownMenu" ref={dropdownMenuRef}>
					{allStatusOptions.map((option, index) => {
						// Handle both {label: "text"} and {text: "text"} formats
						const optionLabel = option.label || option.text;
						// Show as checked if:
						// 1. Empty array means "All" (initial state or all selected) - show all checked
						// 2. Or if explicitly in the selectedOptions array
						const isChecked = isAllSelected || selectedOptions.includes(
							optionLabel
						);
						return (
							<li key={index} className={`${styles.dropdownItem} dropdownItem`}>
								<label className={styles.checkbox}>
									<input
										type="checkbox"
										checked={isChecked}
										onChange={() =>
											handleCheckboxChange(optionLabel)
										}
										className={styles.hiddenCheckbox}
									/>
									<span
										className={`${
											styles.checkmark
										} icn check ${
											isChecked ? styles.checked : ""
										}`}
									/>
									<span className={styles.labelWithDot}>
										{optionLabel}
										<span
											className={`${
												styles.dot
											} ${labelToClassName(
												optionLabel
											)}`}
										/>
									</span>
								</label>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};

export default MultiStatusSelect;
